import React, { useState, useEffect } from 'react';
import { ArrowRight, Calendar, Percent } from 'lucide-react';
import { Badge } from './ui/badge';
import axios from 'axios';
import OptimizedImage from './OptimizedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LatestCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await axios.get(`${API}/pages?page_type=campaign&published_only=true`);
        const data = Array.isArray(response.data) ? response.data : response.data.pages || [];
        setCampaigns(data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  if (loading || campaigns.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-900/80 to-black" data-testid="latest-campaigns-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="bg-purple-600/20 text-purple-400 border-purple-600/50 mb-4 px-3 py-1 text-xs">
            <Percent className="w-3.5 h-3.5 inline mr-1.5" />
            Kampanjer & Tilbud
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent">
              Aktive Kampanjer
            </span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Se våre siste tilbud og spesialkampanjer
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {campaigns.map((campaign) => (
            <a
              key={campaign.id || campaign.slug}
              href={`/kampanje/${campaign.slug}`}
              className="group block bg-gradient-to-br from-purple-900/20 to-gray-900/60 border border-purple-700/30 rounded-xl overflow-hidden hover:border-purple-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-purple-900/20"
              data-testid={`campaign-card-${campaign.slug}`}
            >
              {campaign.featured_image && (
                <div className="h-44 overflow-hidden relative">
                  <OptimizedImage
                    src={campaign.featured_image}
                    alt={campaign.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg text-xs">
                      <Percent className="w-3 h-3 mr-1" />
                      TILBUD
                    </Badge>
                  </div>
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center text-xs text-gray-500 mb-3">
                  <Calendar className="w-3.5 h-3.5 mr-1.5" />
                  {new Date(campaign.published_date || campaign.created_at).toLocaleDateString('no-NO', {
                    day: 'numeric', month: 'short', year: 'numeric'
                  })}
                </div>
                <h3 className="text-white font-semibold mb-2 group-hover:text-purple-300 transition-colors line-clamp-2">
                  {campaign.title}
                </h3>
                {campaign.excerpt && (
                  <p className="text-gray-400 text-sm line-clamp-2">{campaign.excerpt}</p>
                )}
                <div className="flex items-center text-purple-400 group-hover:text-purple-300 font-semibold text-sm mt-4">
                  <span>Se kampanje</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="text-center mt-10">
          <a
            href="/kampanjer"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 hover:border-purple-500/40 transition-all"
            data-testid="view-all-campaigns-link"
          >
            Se alle kampanjer
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default LatestCampaigns;
